import { useEffect, useState } from "react";

/**
 * The ONE Facebook page the shop is working on, shared by the chat inbox, the all-posts screen
 * and the Chat badge in the side menu: pick a page on either screen and the other opens on it,
 * and the badge counts that page.
 *
 * Kept by page id alone. Page ids are Facebook's and unique, and the two screens read the shop
 * id from different places (SHOP_DATA.id vs SHOP), so a shop-keyed entry could silently split
 * in two. A page the current shop does not have is simply ignored by the screen reading it.
 *
 * A convenience only: storage can be unavailable (private mode, blocked site data), and then
 * each screen starts on its first page as before.
 */
const KEY = "SELECTED_FB_PAGE";
//* Where each screen kept its own choice before this; read once so nobody loses theirs.
const OLD_PREFIXES = ["INBOX_PAGE_", "ALLPOSTS_PAGE_"];

const listeners = new Set();

export const readSelectedPage = () => {
    try {
        const v = localStorage.getItem(KEY);
        if (v) return v;
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && OLD_PREFIXES.some((p) => k.startsWith(p))) return localStorage.getItem(k);
        }
    } catch (e) { /* not remembered */ }
    return null;
};

export const saveSelectedPage = (pageId) => {
    if (!pageId) return;
    const id = String(pageId);
    try { localStorage.setItem(KEY, id); } catch (e) { /* not remembered, no harm */ }
    listeners.forEach((fn) => { try { fn(id); } catch (e) { /* one listener must not stop the rest */ } });
};

/** Called with the new page id whenever it changes, here or in another tab. */
export const onSelectedPageChange = (fn) => {
    listeners.add(fn);
    const onStorage = (e) => { if (e.key === KEY && e.newValue) fn(e.newValue); };
    window.addEventListener("storage", onStorage);
    return () => { listeners.delete(fn); window.removeEventListener("storage", onStorage); };
};

export const useSelectedPage = () => {
    const [id, setId] = useState(readSelectedPage);
    useEffect(() => onSelectedPageChange(setId), []);
    return id;
};
